import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MessageSquare, TrendingUp, Star, Zap, AlertCircle, CheckCircle2, Clock, ArrowUp, ArrowDown, Check, X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminFeedbackAnalysis = () => {
  const [stats, setStats] = useState(null);
  const [allFeedback, setAllFeedback] = useState([]);
  const [latestAnalysis, setLatestAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [improvementStatuses, setImprovementStatuses] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchFeedbackData();
  }, []);

  const fetchFeedbackData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats
      const statsRes = await axios.get(`${API}/feedback/stats`, { headers });
      setStats(statsRes.data);

      // Fetch all feedback
      const feedbackRes = await axios.get(`${API}/feedback/all?limit=100`, { headers });
      setAllFeedback(feedbackRes.data.feedback || []);

      // Fetch latest analysis
      const analysisRes = await axios.get(`${API}/feedback/analysis/latest`, { headers });
      if (analysisRes.data && !analysisRes.data.message) {
        setLatestAnalysis(analysisRes.data);
        // Load improvement statuses if they exist
        if (analysisRes.data.improvement_statuses) {
          setImprovementStatuses(analysisRes.data.improvement_statuses);
        }
      }
      
      // Load improvement statuses from DB
      try {
        const statusRes = await axios.get(`${API}/feedback/improvement-statuses`, { headers });
        if (statusRes.data) {
          setImprovementStatuses(statusRes.data);
        }
      } catch {
        // Ignore - endpoint might not exist yet
      }
    } catch (error) {
      console.error('Failed to fetch feedback data', error);
      toast.error('Fehler beim Laden der Feedback-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeFeedback = async () => {
    try {
      setAnalyzing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/feedback/analyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Feedback erfolgreich analysiert!');
      setLatestAnalysis({ 
        analysis: response.data.analysis, 
        created_at: new Date().toISOString() 
      });
    } catch (error) {
      console.error('Analysis failed', error);
      toast.error('Fehler bei der Analyse: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMarkImprovement = async (improvementIndex, status) => {
    const key = `improvement_${improvementIndex}`;
    setActionLoading(key);
    
    try {
      const token = localStorage.getItem('token');
      const improvement = latestAnalysis?.analysis?.improvements?.[improvementIndex];
      
      await axios.post(
        `${API}/feedback/improvement-status`,
        {
          improvement_index: improvementIndex,
          improvement_title: improvement?.title,
          status: status, // 'completed' or 'rejected'
          improvement_data: improvement
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImprovementStatuses(prev => ({
        ...prev,
        [improvementIndex]: {
          status,
          updated_at: new Date().toISOString(),
          title: improvement?.title
        }
      }));
      
      toast.success(
        status === 'completed' 
          ? `✓ "${improvement?.title}" als erledigt markiert` 
          : `✗ "${improvement?.title}" wurde abgelehnt`
      );
    } catch (error) {
      console.error('Failed to update status', error);
      toast.error('Fehler beim Aktualisieren des Status');
    } finally {
      setActionLoading(null);
    }
  };

  const getImprovementStatus = (index) => {
    return improvementStatuses[index]?.status || null;
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className="w-4 h-4"
        style={{ 
          color: i < rating ? '#F59E0B' : '#4B5563',
          fill: i < rating ? '#F59E0B' : 'none'
        }}
      />
    ));
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#94A3B8';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return <ArrowUp className="w-4 h-4" />;
      case 'medium':
        return <AlertCircle className="w-4 h-4" />;
      case 'low':
        return <ArrowDown className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <p style={{ color: '#94A3B8' }}>Lädt Feedback-Daten...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Feedback-Analyse
          </h2>
          <p style={{ color: '#94A3B8' }}>
            Analysieren Sie Benutzerfeedback und erhalten Sie KI-gestützte Verbesserungsvorschläge
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>Gesamt Feedback</p>
                    <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                      {stats.total_feedback}
                    </p>
                  </div>
                  <MessageSquare className="w-10 h-10" style={{ color: '#7CD4FD' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>Durchschn. Bewertung</p>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-3xl font-bold" style={{ color: '#E6EDF3' }}>
                        {stats.average_rating}
                      </p>
                      <div className="flex gap-0.5">
                        {renderStars(Math.round(stats.average_rating))}
                      </div>
                    </div>
                  </div>
                  <Star className="w-10 h-10" style={{ color: '#F59E0B' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>Letzte 7 Tage</p>
                    <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                      {stats.recent_feedback_count}
                    </p>
                  </div>
                  <TrendingUp className="w-10 h-10" style={{ color: '#10B981' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div>
                  <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>Verteilung</p>
                  <div className="space-y-1">
                    {[5, 4, 3, 2, 1].map(rating => (
                      <div key={rating} className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: '#94A3B8' }}>{rating}★</span>
                        <div className="flex-1 h-2 rounded" style={{ backgroundColor: '#1F2937' }}>
                          <div
                            className="h-full rounded"
                            style={{
                              backgroundColor: '#7CD4FD',
                              width: `${(stats.rating_distribution[`${rating}_stars`] / stats.total_feedback * 100) || 0}%`
                            }}
                          />
                        </div>
                        <span className="text-xs" style={{ color: '#94A3B8' }}>
                          {stats.rating_distribution[`${rating}_stars`]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Analysis Section */}
        <Card className="mb-8" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle style={{ color: '#E6EDF3' }}>
                <div className="flex items-center gap-2">
                  <Zap className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  KI-Analyse
                </div>
              </CardTitle>
              <Button
                onClick={handleAnalyzeFeedback}
                disabled={analyzing || allFeedback.length === 0}
                style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
              >
                {analyzing ? 'Analysiere...' : 'Neue Analyse starten'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {latestAnalysis ? (
              <div>
                <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                  Letzte Analyse: {new Date(latestAnalysis.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>

                {/* Summary */}
                {latestAnalysis.analysis?.summary && (
                  <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
                    <h4 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Zusammenfassung</h4>
                    <p style={{ color: '#94A3B8' }}>{latestAnalysis.analysis.summary}</p>
                  </div>
                )}

                {/* Improvements */}
                {latestAnalysis.analysis?.improvements && latestAnalysis.analysis.improvements.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>
                      Priorisierte Verbesserungsvorschläge
                    </h4>
                    <div className="space-y-4">
                      {latestAnalysis.analysis.improvements.map((improvement, index) => {
                        const status = getImprovementStatus(index);
                        const isCompleted = status === 'completed';
                        const isRejected = status === 'rejected';
                        
                        return (
                          <div
                            key={index}
                            className="p-4 rounded-lg border-2 transition-all"
                            style={{ 
                              backgroundColor: isCompleted ? '#22C55E10' : isRejected ? '#EF444410' : '#0B0F14',
                              borderColor: isCompleted ? '#22C55E' : isRejected ? '#EF4444' : '#1F2937',
                              opacity: isRejected ? 0.6 : 1
                            }}
                          >
                            <div className="flex items-start gap-3 mb-2">
                              <div
                                className="px-2 py-1 rounded text-sm font-semibold flex items-center gap-1"
                                style={{ 
                                  backgroundColor: `${getPriorityColor(improvement.priority)}20`,
                                  color: getPriorityColor(improvement.priority)
                                }}
                              >
                                {getPriorityIcon(improvement.priority)}
                                {improvement.priority?.toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h5 className="font-semibold mb-1" style={{ 
                                    color: '#E6EDF3',
                                    textDecoration: isRejected ? 'line-through' : 'none'
                                  }}>
                                    {improvement.title}
                                    {isCompleted && <span className="ml-2 text-green-400">✓ Erledigt</span>}
                                    {isRejected && <span className="ml-2 text-red-400">✗ Abgelehnt</span>}
                                  </h5>
                                  
                                  {/* Action Buttons */}
                                  {!status && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleMarkImprovement(index, 'completed')}
                                        disabled={actionLoading === `improvement_${index}`}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                      >
                                        {actionLoading === `improvement_${index}` ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <>
                                            <Check className="w-4 h-4 mr-1" />
                                            Erledigt
                                          </>
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleMarkImprovement(index, 'rejected')}
                                        disabled={actionLoading === `improvement_${index}`}
                                        className="border-red-500 text-red-400 hover:bg-red-500/20"
                                      >
                                        <X className="w-4 h-4 mr-1" />
                                        Ablehnen
                                      </Button>
                                    </div>
                                  )}
                                  
                                  {/* Status Badge with undo option */}
                                  {status && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setImprovementStatuses(prev => {
                                          const newStatuses = { ...prev };
                                          delete newStatuses[index];
                                          return newStatuses;
                                        });
                                      }}
                                      className="text-xs"
                                      style={{ color: '#94A3B8' }}
                                    >
                                      ↩ Rückgängig
                                    </Button>
                                  )}
                                </div>
                                
                                <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                                  {improvement.description}
                                </p>
                              
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                  <div>
                                    <span style={{ color: '#94A3B8' }}>Häufigkeit: </span>
                                    <span className="font-semibold" style={{ color: '#E6EDF3' }}>
                                      {improvement.frequency}×
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#94A3B8' }}>Aufwand: </span>
                                    <span className="font-semibold" style={{ color: '#E6EDF3' }}>
                                      {improvement.effort}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#94A3B8' }}>Wert: </span>
                                    <span className="font-semibold" style={{ color: '#E6EDF3' }}>
                                      {improvement.value}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#94A3B8' }}>Machbarkeit: </span>
                                    <span className="font-semibold" style={{ color: '#E6EDF3' }}>
                                      {improvement.feasibility}
                                    </span>
                                  </div>
                                </div>

                                {improvement.implementation_suggestion && (
                                  <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#1F2937' }}>
                                    <p className="text-xs font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                                      💡 Umsetzungsvorschlag:
                                    </p>
                                    <p className="text-xs" style={{ color: '#94A3B8' }}>
                                      {improvement.implementation_suggestion}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Summary of completed improvements for newsletter */}
                    {Object.values(improvementStatuses).some(s => s.status === 'completed') && (
                      <div className="mt-6 p-4 rounded-lg border-2" style={{ backgroundColor: '#22C55E10', borderColor: '#22C55E' }}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold flex items-center gap-2" style={{ color: '#22C55E' }}>
                            <CheckCircle2 className="w-5 h-5" />
                            Erledigte Verbesserungen (für Newsletter)
                          </h4>
                          <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#22C55E30', color: '#22C55E' }}>
                            {Object.values(improvementStatuses).filter(s => s.status === 'completed').length} erledigt
                          </span>
                        </div>
                        <ul className="space-y-2">
                          {Object.entries(improvementStatuses)
                            .filter(([, s]) => s.status === 'completed')
                            .map(([idx, s]) => (
                              <li key={idx} className="text-sm flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                                <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
                                {s.title || latestAnalysis?.analysis?.improvements?.[idx]?.title}
                              </li>
                            ))}
                        </ul>
                        <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                          💡 Diese Punkte werden automatisch im wöchentlichen Newsletter erwähnt.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Positive Feedback */}
                {latestAnalysis.analysis?.positive_feedback && latestAnalysis.analysis.positive_feedback.length > 0 && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#10B98120', borderLeft: '4px solid #10B981' }}>
                    <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#10B981' }}>
                      <CheckCircle2 className="w-5 h-5" />
                      Positives Feedback
                    </h4>
                    <ul className="space-y-1">
                      {latestAnalysis.analysis.positive_feedback.map((item, index) => (
                        <li key={index} className="text-sm" style={{ color: '#94A3B8' }}>
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Zap className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
                <p style={{ color: '#94A3B8' }}>
                  Noch keine Analyse vorhanden. Klicken Sie auf &quot;Neue Analyse starten&quot;, um eine KI-gestützte Analyse zu erstellen.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Feedback */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Neuestes Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            {allFeedback.length === 0 ? (
              <p style={{ color: '#94A3B8' }}>Noch kein Feedback vorhanden</p>
            ) : (
              <div className="space-y-4">
                {allFeedback.slice(0, 10).map((feedback, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg border"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {renderStars(feedback.rating)}
                        </div>
                        <span className="text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                          {feedback.rating}/5
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
                        <Clock className="w-4 h-4" />
                        {new Date(feedback.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                    </div>

                    {feedback.comment && (
                      <p className="text-sm mb-2" style={{ color: '#E6EDF3' }}>
                        {feedback.comment}
                      </p>
                    )}

                    {feedback.improvements && (
                      <div className="text-xs p-2 rounded" style={{ backgroundColor: '#1F2937', color: '#94A3B8' }}>
                        <span className="font-semibold" style={{ color: '#7CD4FD' }}>Verbesserungsvorschlag: </span>
                        {feedback.improvements}
                      </div>
                    )}

                    <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                      Nutzer: {feedback.user_email} • Session: {feedback.session_id?.substring(0, 8)}...
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
